import type {Context} from "@sentry/core";
import {useEffect} from "react";

import {setContext} from "./khan-sentry.ts";

/**
 * Set a tag on the Sentry scope for the duration of the component's lifecycle.
 *
 * This is useful in situations where code that may capture an error does
 * not have access to the information that needs to be tagged on that error,
 * a the component wrapping that code does have access to that information.
 * For example, Perseus may capture the error, but the code rendering Perseus
 * may wish to attach additional information to such an error.
 *
 * @param name The name of the context to set.
 * @param value The value to set the context to.
 *
 * @example
 * ```tsx
 * import {useSentryContext} from "@repo/logging";
 *
 * const MyComponent = () => {
 *    useSentryContext("my-context", {foo: "bar"});
 *    return <div>My Component</div>;
 * };
 * ```
 */
export const useSentryContext = (name: string, value: Context) => {
    useEffect(() => {
        setContext(name, value);
        return () => {
            setContext(name, null);
        };
    }, [name, value]);
};
